import { parseJwt } from '../../parseJwt';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../Axios/axios';
import TechnicalServicePersonnelShell from './TechnicalServicePersonnelShell';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_META = {
  1: { label: 'Yeni Talep', className: 'new' },
  2: { label: 'Atand\u0131', className: 'assigned' },
  3: { label: '\u0130\u015flemde', className: 'progress' },
  4: { label: 'Tamamland\u0131', className: 'success' },
  5: { label: 'M\u00fcd\u00fcr Onay\u0131 Bekliyor', className: 'warning' },
  6: { label: 'M\u00fcd\u00fcr Onaylad\u0131', className: 'success' },
  7: { label: 'Muhasebeye Aktar\u0131ld\u0131', className: 'accounting' },
  8: { label: '\u0130ptal', className: 'cancelled' }
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const getStatusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META[1];

const getJobId = (job) => String(job?.id || job?.Id || '');

const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return '';

  try {
    const decoded = parseJwt(token);
    return (
      decoded.sub ||
      decoded.nameid ||
      decoded.userId ||
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      ''
    );
  } catch (error) {
    console.error('Token parse edilemedi:', error);
    return '';
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const TechnicalServiceCompletedJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchJobs = useCallback(async () => {
    const userId = getUserIdFromToken();
    const response = await api.get('/TechnicalService/requests/my', {
      params: userId ? { userId } : {}
    });

    const items = response?.data?.data || [];
    setJobs(items);
    return items;
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const items = await fetchJobs();
        const completedItems = items.filter((job) => normalizeStatus(job.status) >= 4);
        if (completedItems.length > 0) {
          setSelectedJobId(getJobId(completedItems[0]));
        }
      } catch (error) {
        console.error('Tamamlanan i\u015fler al\u0131namad\u0131:', error);
        setErrorMessage('Tamamlanan talepler y\u00fcklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [fetchJobs]);

  const completedJobCards = useMemo(
    () =>
      jobs
        .map((job) => ({
          ...job,
          normalizedStatus: normalizeStatus(job.status),
          statusMeta: getStatusMeta(job.status),
          machineLabel: job.towerCraneSerialNo || job.externalMachineName || '-',
          workSiteLabel: job.workSiteName || job.externalWorkSiteName || '-'
        }))
        .filter((job) => job.normalizedStatus >= 4),
    [jobs]
  );

  const selectedJob =
    completedJobCards.find((job) => getJobId(job) === selectedJobId) || completedJobCards[0] || null;

  return (
    <TechnicalServicePersonnelShell title={'Tamamlad\u0131klar\u0131m'} notificationCount={0}>
        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}

        <section className="service-personnel-dashboard completed-dashboard">
          <div className="service-panel service-jobs-panel">
            <div className="service-section-title">
              <div>
                <h2>{'Tamamlanan \u0130\u015fler'}</h2>
                <p>{'Ge\u00e7mi\u015f servis formlar\u0131n\u0131 sat\u0131r halinde incele.'}</p>
              </div>
              {!loading && <span className="service-count-pill">{`${completedJobCards.length} kay\u0131t`}</span>}
            </div>

            {loading ? (
              <p>{'Tamamlanan talepler y\u00fckleniyor...'}</p>
            ) : completedJobCards.length > 0 ? (
              <div className="service-job-list">
                {completedJobCards.map((job) => (
                  <button
                    key={getJobId(job)}
                    type="button"
                    className={`service-job-row completed polished ${getJobId(job) === selectedJobId ? 'active' : ''}`}
                    onClick={() => setSelectedJobId(getJobId(job))}
                  >
                    <div className="service-job-info">
                      <span>{job.requestNo || `Talep #${job.id}`}</span>
                      <strong>{job.subject}</strong>
                      <small>{`Firma: ${job.requestingCompanyName || '-'}`}</small>
                      <small>{`Konum: ${job.workSiteLocation || job.workSiteLabel || '-'}`}</small>
                    </div>
                    <div className="service-job-status">
                      <span className={`badge ${job.statusMeta.className}`}>{job.statusMeta.label}</span>
                    </div>
                    <div className="service-job-date">
                      <span>{formatDate(job.operationDate)}</span>
                    </div>
                    <div className="service-job-open detail-only">
                      {'Detay'}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p>{'Hen\u00fcz tamamlad\u0131\u011f\u0131n teknik servis talebi bulunmuyor.'}</p>
            )}
          </div>

          <aside className="service-panel service-completed-detail">
            <h3>{'Talep Detay\u0131'}</h3>
            {selectedJob ? (
              <div className="request-summary-grid compact top-space">
                <div><strong>Talep No:</strong> {selectedJob.requestNo}</div>
                <div><strong>Firma:</strong> {selectedJob.requestingCompanyName}</div>
                <div><strong>Makine:</strong> {selectedJob.machineLabel}</div>
                <div><strong>{'\u015eantiye:'}</strong> {selectedJob.workSiteLabel}</div>
                <div><strong>Durum:</strong> {selectedJob.statusMeta.label}</div>
                <div><strong>Konum:</strong> {selectedJob.workSiteLocation || '-'}</div>
                <div><strong>Operasyon:</strong> {formatDate(selectedJob.operationDate)}</div>
                <div><strong>Talep Tarihi:</strong> {formatDate(selectedJob.requestedDate)}</div>
              </div>
            ) : (
              <p>{'Bir tamamlanan i\u015f se\u00e7ildi\u011finde detay\u0131 burada g\u00f6r\u00fcn\u00fcr.'}</p>
            )}
          </aside>
        </section>
    </TechnicalServicePersonnelShell>
  );
};

export default TechnicalServiceCompletedJobsPage;
